var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var OwnWorkView_1;
import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { marked } from "marked";
import { SignalWatcher, effect } from "@lit-labs/preact-signals";
import { scenario, draft, launch } from "../state";
import { api, LockRefusedError } from "../api/client";
import "../shell/chart-navigator";
import "../components/chart-viewer";
import "../modes/abstract";
import "../components/own-work-audit-record";
const PHASES = ["Briefing", "Identify the audit", "Code the records", "Prepare the records", "Audit your coding", "Respond to the findings"];
const blankDraft = () => ({ codes: [], episodeMeta: {}, navigateAnswers: {}, saveStatus: "idle" });
const blankState = () => ({
    phase: 0, coding: {}, identify: { purposes: [] }, prep: {}, audit: {},
    arithmetic: { errors: "", items: "", rate_pct: "" }, review: { corrected: {}, actions: [] },
});
const md = (text) => unsafeHTML(marked.parse(text ?? "", { async: false }));
let OwnWorkView = class OwnWorkView extends SignalWatcher(LitElement) {
    constructor() {
        super(...arguments);
        this.batch = null;
        this.st = blankState();
        this.recordIdx = 0;
        this.selectedDocId = null;
        this.saveStatus = "idle";
        this.busy = false;
        this.notice = "";
        this.lockProblems = null;
        this.confirmLock = false;
        this.confirmAudit = false;
        this.auditResult = null;
        this.reviewResult = null;
        this.supportState = "idle";
        this._stopEffect = null;
        this._switching = false;
        this._saveTimer = null;
        this.attemptStart = Date.now();
    }
    static { OwnWorkView_1 = this; }
    static { this.styles = css `
    :host, *, *::before, *::after { box-sizing: border-box; }
    :host { display: grid; grid-template-rows: auto 1fr auto; height: 100vh; container-type: inline-size; container-name: ehr;
            border-radius: 10px; overflow: hidden; background: var(--color-slate-50, #f8fafc); font-size: 13px;
            color: var(--color-slate-700); }
    .stepper { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 10px 16px; background: var(--color-white);
               border-bottom: 1px solid var(--color-slate-200); }
    .stepper .title { font-weight: 700; font-size: 14px; color: var(--color-slate-800); margin-right: 6px; }
    .steps { display: flex; gap: 4px; flex-wrap: wrap; margin-left: auto; }
    .step { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; font-size: 12px;
            border: 1px solid var(--color-slate-300); color: var(--color-slate-600); background: var(--color-white); }
    .step.current { background: var(--color-blue-600, #2563eb); border-color: var(--color-blue-600, #2563eb); color: white; }
    .step.done { border-color: var(--color-green-500, #16a34a); color: var(--color-green-600, #15803d); }
    .step .n { font-weight: 700; }
    .body { display: grid; grid-template-columns: 184px minmax(0, 1fr) minmax(0, 440px); overflow: hidden; min-height: 0; }
    .body.single { grid-template-columns: minmax(0, 1fr); }
    .pane { overflow: auto; min-height: 0; min-width: 0; }
    .pane.nav { background: var(--color-slate-100); border-right: 1px solid var(--color-slate-200); }
    .pane.chart { background: var(--color-white); padding: 18px 20px; }
    .pane.panel { background: var(--color-slate-50); border-left: 1px solid var(--color-slate-200); padding: 16px; }
    .pane.single { max-width: 820px; margin: 0 auto; padding: 24px 28px; width: 100%; }
    @container ehr (max-width: 759px) {
      .body { display: block; overflow-y: auto; overflow-x: hidden; background: var(--color-slate-200); }
      .pane { overflow: visible; border: 0; }
      .pane + .pane { margin-top: 8px; }
    }
    .actions { display: flex; gap: 12px; align-items: center; padding: 12px 16px; background: var(--color-white);
               border-top: 1px solid var(--color-slate-200); }
    .spacer { flex: 1; }
    .save, .notice { font-size: 12px; color: var(--color-slate-500); }
    .notice.err { color: #dc2626; max-width: 420px; }
    button { font-family: inherit; }
    button.nav-btn { padding: 9px 16px; border-radius: 6px; background: var(--color-white); color: var(--color-slate-700);
                     border: 1px solid var(--color-slate-300); cursor: pointer; font-weight: 600; }
    button.nav-btn:disabled { opacity: .5; cursor: default; }
    button.primary { padding: 10px 20px; border-radius: 6px; background: var(--color-blue-600, #2563eb); color: white;
                     border: 0; cursor: pointer; font-weight: 600; }
    button.primary:disabled { opacity: .6; cursor: default; }
    .card { background: var(--color-white); border: 1px solid var(--color-slate-200); border-radius: 10px; padding: 18px 20px;
            margin-bottom: 14px; }
    .card h2 { margin: 0 0 8px; font-size: 17px; color: var(--color-slate-900); }
    .card h3 { margin: 14px 0 6px; font-size: 13px; text-transform: uppercase; letter-spacing: .05em; color: var(--color-slate-700); }
    .prose { line-height: 1.55; }
    .prose p { margin: 0 0 10px; }
    .prose ol, .prose ul { padding-left: 20px; }
    .q { margin: 12px 0; }
    .q .prompt { font-weight: 600; color: var(--color-slate-800); margin-bottom: 6px; }
    .q label { display: flex; align-items: center; gap: 8px; padding: 4px 0; cursor: pointer; }
    .records { display: flex; gap: 6px; margin-bottom: 10px; flex-wrap: wrap; }
    .records button { padding: 5px 12px; border-radius: 999px; border: 1px solid var(--color-slate-300); background: white;
                      cursor: pointer; font-size: 12px; color: var(--color-slate-700); }
    .records button.current { background: var(--color-slate-800); border-color: var(--color-slate-800); color: white; }
    .records button.done { border-color: var(--color-green-500, #16a34a); }
    .doc { display: grid; grid-template-columns: 1fr auto; gap: 8px; align-items: center; padding: 8px 0;
           border-bottom: 1px solid var(--color-slate-100); }
    .doc .name { font-weight: 600; color: var(--color-slate-800); }
    .doc .meta { font-size: 11px; color: var(--color-slate-500); }
    .doc select { font: inherit; font-size: 12px; padding: 4px 6px; border-radius: 4px; border: 1px solid var(--color-slate-300); }
    .locked-list div { padding: 3px 0; font-family: var(--font-mono); font-size: 12px; }
    .arith { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
    .arith label { display: flex; flex-direction: column; gap: 3px; font-size: 11px; text-transform: uppercase; letter-spacing: .04em;
                   font-weight: 600; color: var(--color-slate-500); }
    .arith input { font: inherit; font-size: 13px; padding: 6px 8px; border-radius: 4px; border: 1px solid var(--color-slate-300); }
    .result-item { display: grid; grid-template-columns: 22px 1fr; gap: 8px; padding: 8px 0; border-bottom: 1px solid var(--color-slate-100); }
    .ri-icon { font-weight: 700; }
    .ri-icon.ok { color: var(--color-green-600, #15803d); } .ri-icon.warn { color: var(--color-amber-600, #d97706); } .ri-icon.wrong { color: #dc2626; }
    .ri-field { font-weight: 600; color: var(--color-slate-800); }
    .ri-msg { color: var(--color-slate-600); }
    .score { font-size: 28px; font-weight: 700; color: var(--color-slate-900); }
    .pill { display: inline-block; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 10px; text-transform: uppercase; }
    .pill.pass { background: #dcfce7; color: #166534; } .pill.fail { background: #fee2e2; color: #991b1b; }
    table { border-collapse: collapse; width: 100%; font-size: 12px; }
    th, td { text-align: left; padding: 5px 6px; border-bottom: 1px solid var(--color-slate-100); vertical-align: top; }
    th { color: var(--color-slate-500); font-weight: 600; text-transform: uppercase; letter-spacing: .04em; font-size: 11px; }
    .mono { font-family: var(--font-mono); }
    .err { color: #991b1b; }
    .ok { color: #166534; }
    .confirm-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,.6); display: grid; place-items: center; z-index: 200; }
    .confirm-modal { background: var(--color-white); padding: 22px; border-radius: 8px; max-width: 460px; width: 90vw;
                     box-shadow: 0 12px 32px rgba(0,0,0,.25); }
    .confirm-modal h2 { margin: 0 0 10px; font-size: 16px; color: var(--color-slate-900); }
    .confirm-modal p { color: var(--color-slate-600); line-height: 1.5; }
    .confirm-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; }
  `; }
    // ---------------------------------------------------------------------
    // lifecycle + persistence
    // ---------------------------------------------------------------------
    async connectedCallback() {
        super.connectedCallback();
        if (!this.batch)
            return;
        let saved = {};
        try {
            const d = await api.loadDraft(this.batch.id);
            saved = (d?.draft?.own_work) ?? {};
        }
        catch { /* no saved state */ }
        const st = { ...blankState(), ...saved };
        for (const ep of this.batch.episodes) {
            st.coding[ep.scenario_id] = st.coding[ep.scenario_id] ?? blankDraft();
            st.prep[ep.scenario_id] = st.prep[ep.scenario_id] ?? {};
            st.audit[ep.scenario_id] = st.audit[ep.scenario_id] ?? { changes: [] };
        }
        this.st = this.reconcilePhase(st);
        this.activateRecord(0);
        this._stopEffect = effect(() => {
            const d = draft.value;
            if (this._switching || !d)
                return;
            const ep = this.batch?.episodes[this.recordIdx];
            if (!ep)
                return;
            if (this.st.phase === 2 && this.batch?.run.status === "draft") {
                this.st.coding[ep.scenario_id] = d;
                this.scheduleSave();
                this.requestUpdate();
            }
            else if (this.st.phase === 5) {
                this.st.review.corrected[ep.scenario_id] = d;
                this.scheduleSave();
                this.requestUpdate();
            }
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._stopEffect)
            this._stopEffect();
        if (this._saveTimer)
            clearTimeout(this._saveTimer);
    }
    /** The server's run status always wins over the saved phase. */
    reconcilePhase(st) {
        const status = this.batch.run.status;
        if (status === "completed" || status === "passed")
            st.phase = 5;
        else if (status === "locked")
            st.phase = Math.max(st.phase, 3);
        else
            st.phase = Math.min(st.phase, 2);
        if (status === "passed" || status === "completed") {
            const reveal = this.batch.run.reveal;
            for (const ep of this.batch.episodes) {
                if (!st.review.corrected[ep.scenario_id]?.codes?.length && reveal) {
                    st.review.corrected[ep.scenario_id] = { ...blankDraft(), codes: reveal.records[ep.scenario_id].corrected };
                }
            }
        }
        return st;
    }
    update_(patch) {
        this.st = { ...this.st, ...patch };
        this.scheduleSave();
    }
    scheduleSave() {
        this.saveStatus = "saving";
        if (this._saveTimer)
            clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(() => void this.save(), 1200);
    }
    async save() {
        if (!this.batch)
            return;
        try {
            await api.saveDraft(this.batch.id, { own_work: this.st });
            this.saveStatus = "saved";
        }
        catch {
            this.saveStatus = "error";
        }
    }
    activateRecord(i) {
        const ep = this.batch?.episodes[i];
        if (!ep)
            return;
        this._switching = true;
        this.recordIdx = i;
        this.selectedDocId = null;
        scenario.value = ep;
        if (this.st.phase === 5)
            draft.value = (this.st.review.corrected[ep.scenario_id] ?? blankDraft());
        else
            draft.value = (this.st.coding[ep.scenario_id] ?? blankDraft());
        this._switching = false;
    }
    setPhase(phase) {
        this.update_({ phase });
        this.auditResult = this.auditResult && phase === 4 ? this.auditResult : null;
        this.notice = "";
        this.activateRecord(0);
    }
    get ep() { return this.batch?.episodes[this.recordIdx] ?? null; }
    // ---------------------------------------------------------------------
    // phase actions
    // ---------------------------------------------------------------------
    identifyComplete() {
        const i = this.st.identify;
        return !!(i.scope && i.timing && i.selection && i.purposes.length);
    }
    codingSummary() {
        return this.batch.episodes.map((ep) => {
            const codes = this.st.coding[ep.scenario_id]?.codes ?? [];
            const dx = codes.filter((c) => c.type === "Pdx" || c.type === "Adx").length;
            return { ep, total: codes.length, dx, ready: codes.some((c) => c.type === "Pdx") && codes.length >= 3 };
        });
    }
    async lock() {
        if (!this.batch || this.busy)
            return;
        this.busy = true;
        this.lockProblems = null;
        this.notice = "";
        try {
            await this.save();
            const drafts = {};
            for (const ep of this.batch.episodes) {
                const d = this.st.coding[ep.scenario_id] ?? blankDraft();
                drafts[ep.scenario_id] = { codes: d.codes, episodeMeta: d.episodeMeta };
            }
            const r = await api.lockCoding(this.batch.id, drafts);
            this.batch = { ...this.batch, run: { ...this.batch.run, status: "locked", locked_codes: r.locked_codes } };
            this.confirmLock = false;
            this.setPhase(3);
        }
        catch (e) {
            if (e instanceof LockRefusedError) {
                this.lockProblems = e.problems;
                this.confirmLock = false;
            }
            else {
                console.error("lock failed:", e);
                this.notice = "Your coding is saved but could not be locked just now. Check your connection and try again.";
            }
        }
        finally {
            this.busy = false;
        }
    }
    prepComplete() {
        return this.batch.episodes.every((ep) => ep.documents.every((d) => !!this.st.prep[ep.scenario_id]?.[d.id]));
    }
    auditDecisionsComplete() {
        const missing = [];
        for (const ep of this.batch.episodes) {
            const locked = this.batch.run.locked_codes?.[ep.scenario_id] ?? [];
            const changes = this.st.audit[ep.scenario_id]?.changes ?? [];
            for (const c of locked) {
                if (!changes.some((ch) => ch.code === c.code && ch.action !== "reorder" && ch.action !== "add"))
                    missing.push(`${ep.episode_title}: ${c.code}`);
            }
            for (const ch of changes) {
                if (ch.action !== "confirm" && ch.action !== "reorder" && !ch.error_type)
                    missing.push(`${ep.episode_title}: error type for ${ch.code}`);
                if (ch.action === "reorder" && !ch.error_type)
                    missing.push(`${ep.episode_title}: error type for moving ${ch.code}`);
            }
        }
        return { ok: missing.length === 0, missing };
    }
    arithmeticComplete() {
        const a = this.st.arithmetic;
        return a.errors !== "" && a.items !== "" && a.rate_pct !== "";
    }
    async submitAudit() {
        if (!this.batch || this.busy)
            return;
        this.busy = true;
        this.notice = "";
        this.confirmAudit = false;
        try {
            await this.save();
            const payload = {
                identify: this.st.identify,
                prep: this.st.prep,
                audit: this.st.audit,
                arithmetic: { errors: Number(this.st.arithmetic.errors), items: Number(this.st.arithmetic.items),
                    rate_pct: Number(this.st.arithmetic.rate_pct) },
                _elapsed_sec: Math.max(0, Math.round((Date.now() - this.attemptStart) / 1000)),
            };
            const r = await api.submitOwnWorkAudit(this.batch.id, payload);
            this.auditResult = r;
            const blind = { ...this.batch.run.blind, ...(r.assessment ?? {}),
                ...(r.locked ? { locked: true, attempts_used: r.attempts_used ?? 3, attempts_remaining: 0 } : {}) };
            if (r.passed && r.reveal) {
                this.batch = { ...this.batch, run: { ...this.batch.run, status: "passed", blind, reveal: r.reveal } };
                this.st = this.reconcilePhase({ ...this.st, phase: 5 });
                this.scheduleSave();
                this.activateRecord(0);
            }
            else {
                this.batch = { ...this.batch, run: { ...this.batch.run, blind } };
            }
            this.attemptStart = Date.now();
        }
        catch (e) {
            console.error("audit submit failed:", e);
            this.notice = "Your audit is saved but could not be submitted just now. Check your connection and try again.";
        }
        finally {
            this.busy = false;
        }
    }
    reviewComplete() {
        const r = this.st.review;
        return !!r.within_rate && r.actions.length > 0 && (!r.actions.includes("name_gap") || !!r.recurring_gap);
    }
    async submitReview() {
        if (!this.batch || this.busy)
            return;
        this.busy = true;
        this.notice = "";
        try {
            await this.save();
            const corrected = {};
            for (const ep of this.batch.episodes)
                corrected[ep.scenario_id] = { codes: this.st.review.corrected[ep.scenario_id]?.codes ?? [] };
            const r = await api.submitOwnWorkReview(this.batch.id, {
                corrected, within_rate: this.st.review.within_rate, actions: this.st.review.actions,
                recurring_gap: this.st.review.recurring_gap ?? null,
            });
            this.reviewResult = r;
            if (r.complete)
                this.batch = { ...this.batch, run: { ...this.batch.run, status: "completed", review: { completed: true, last: r } } };
        }
        catch (e) {
            console.error("review submit failed:", e);
            this.notice = "Your response is saved but could not be submitted just now. Check your connection and try again.";
        }
        finally {
            this.busy = false;
        }
    }
    static { this.LMS_ORIGIN = "https://my.talentmed.edu.au"; }
    continueToNext() {
        const msg = { source: "tm-ehr-sim", action: "reload" };
        try {
            window.parent?.postMessage(msg, OwnWorkView_1.LMS_ORIGIN);
        }
        catch { /* noop */ }
    }
    async openSupport() {
        if (this.supportState === "sending" || this.supportState === "sent")
            return;
        const name = (launch.value?.actor?.name ?? "").trim();
        const email = (launch.value?.actor?.mbox ?? "").replace(/^mailto:/, "");
        const activity = this.batch?.title ?? "Coding audit of your own work";
        this.supportState = "sending";
        try {
            await api.requestUnlock({
                subject: `Assessment unlock request: ${activity}${name ? ` (${name})` : ""}`,
                description: "Hi Support Team,\n\nI have used all my attempts at the audit step of this activity and would like it reset so I can have another go.\n\n"
                    + `Activity: ${activity}\nActivity reference: ${this.batch?.id}\nStudent: ${name || email}\nEmail: ${email}\n\nThanks very much.`,
                name,
            });
            this.supportState = "sent";
        }
        catch {
            this.supportState = "error";
        }
    }
    // ---------------------------------------------------------------------
    // rendering
    // ---------------------------------------------------------------------
    renderStepper() {
        const status = this.batch.run.status;
        const doneUpTo = status === "completed" ? 6 : status === "passed" ? 4 : status === "locked" ? 2 : this.st.phase - 1;
        return html `<div class="stepper">
      <span class="title">${this.batch.title}</span>
      <div class="steps">
        ${PHASES.map((p, i) => i === 0 ? nothing : html `<span class="step ${i === this.st.phase ? "current" : ""} ${i <= doneUpTo ? "done" : ""}">
          <span class="n">${i}</span> ${p}</span>`)}
      </div>
    </div>`;
    }
    renderRecordTabs(doneFn) {
        return html `<div class="records">
      ${this.batch.episodes.map((ep, i) => html `<button type="button" class="${i === this.recordIdx ? "current" : ""} ${doneFn(ep) ? "done" : ""}"
          @click=${() => this.activateRecord(i)}>${i + 1}. ${ep.episode_title}</button>`)}
    </div>`;
    }
    renderChartPanes() {
        const ep = this.ep;
        const docs = ep.documents ?? [];
        const currentDoc = (this.selectedDocId ? docs.find((d) => d.id === this.selectedDocId) : docs[0]) ?? null;
        return html `
      <div class="pane nav">
        ${docs.length ? html `<chart-navigator .documents=${docs} .selectedId=${this.selectedDocId}
            @doc-focus=${((e) => { this.selectedDocId = e.detail.docId; })}></chart-navigator>`
            : html `<p style="padding:16px">No chart documents.</p>`}
      </div>
      <div class="pane chart">
        ${currentDoc ? html `<chart-viewer .document=${currentDoc}></chart-viewer>` : html `<p>Select a document.</p>`}
      </div>`;
    }
    renderBriefing() {
        return html `<div class="body single"><div class="pane single">
      <div class="card"><h2>${this.batch.title}</h2><div class="prose">${md(this.batch.intro_md)}</div></div>
    </div></div>`;
    }
    renderIdentify() {
        const q = this.batch.own_work.identify;
        const i = this.st.identify;
        const single = (key) => html `<div class="q">
      <div class="prompt">${q[key].prompt}</div>
      ${q[key].options.map((o) => html `<label><input type="radio" name=${key} value=${o.id} .checked=${i[key] === o.id}
          @change=${() => this.update_({ identify: { ...this.st.identify, [key]: o.id } })}> ${o.label}</label>`)}
    </div>`;
        return html `<div class="body single"><div class="pane single">
      <div class="card"><h2>Step 1. Identify the audit</h2>
        <p>Read the procedure, then answer the questions below it.</p>
        <div class="prose">${md(this.batch.own_work.procedure_md)}</div></div>
      <div class="card">
        ${single("scope")}${single("timing")}${single("selection")}
        <div class="q"><div class="prompt">${q.purposes.prompt}</div>
          ${q.purposes.options.map((o) => html `<label><input type="checkbox" value=${o.id} .checked=${i.purposes.includes(o.id)}
              @change=${(e) => {
            const on = e.target.checked;
            const cur = this.st.identify;
            const purposes = on ? [...cur.purposes.filter((p) => p !== o.id), o.id] : cur.purposes.filter((p) => p !== o.id);
            this.update_({ identify: { ...cur, purposes } });
        }}> ${o.label}</label>`)}
        </div>
      </div>
    </div></div>`;
    }
    renderCode() {
        const ep = this.ep;
        const status = this.batch.run.status;
        const locked = this.batch.run.locked_codes?.[ep.scenario_id];
        return html `<div class="body">
      ${this.renderChartPanes()}
      <div class="pane panel">
        ${this.renderRecordTabs((e) => (this.st.coding[e.scenario_id]?.codes?.length ?? 0) >= 3)}
        ${status === "draft"
            ? html `<p class="notice" style="margin:0 0 10px">Step 2. Code this record as you would at work. No marks or feedback are shown here.
                   When all three records are coded, lock your coding.</p>
                 <mode-abstract .edition=${ep.edition ?? "13e"} .episodePanel=${{ hmv: false, weight: false, query: false }}></mode-abstract>`
            : html `<div class="card"><h2>Coding locked</h2><p>This is the coding you locked for this record. It cannot be edited; it is the work you audit in step 4.</p>
                 <div class="locked-list">${(locked ?? []).map((c) => html `<div>${c.code} ${c.type}${c.cof ? ` COF ${c.cof}` : ""} ${c.description ?? ""}</div>`)}</div></div>`}
        ${this.lockProblems ? html `<div class="card"><h3 class="err">Not ready to lock</h3>
          ${Object.entries(this.lockProblems).map(([cid, probs]) => html `<div><b>${this.batch.episodes.find((e) => e.scenario_id === cid)?.episode_title ?? cid}</b>: ${probs.join("; ")}</div>`)}
          <p>A refused lock is not an attempt. Finish the coding and try again.</p></div>` : nothing}
      </div>
    </div>`;
    }
    renderPrepare() {
        const ep = this.ep;
        const prep = this.st.prep[ep.scenario_id] ?? {};
        const set = (docId, v) => this.update_({ prep: { ...this.st.prep, [ep.scenario_id]: { ...(this.st.prep[ep.scenario_id] ?? {}), [docId]: v } } });
        return html `<div class="body">
      ${this.renderChartPanes()}
      <div class="pane panel">
        ${this.renderRecordTabs((e) => e.documents.every((d) => !!this.st.prep[e.scenario_id]?.[d.id]))}
        <div class="card"><h2>Step 3. Prepare the record</h2>
          <p>Before an audit, check that every document belongs to this patient and this episode, and that it is complete.
            Include documents that belong and are ready. Exclude documents that do not belong. Flag a document that belongs but is not ready.</p>
          ${ep.documents.map((d) => html `<div class="doc">
            <div><div class="name">${d.category ?? d.type ?? d.id}</div><div class="meta">${d.id} · ${d.type ?? ""}</div></div>
            <select aria-label="Decision for ${d.id}" .value=${prep[d.id] ?? ""} @change=${(e) => set(d.id, e.target.value)}>
              <option value="">Decide</option>
              <option value="include" ?selected=${prep[d.id] === "include"}>Include</option>
              <option value="exclude" ?selected=${prep[d.id] === "exclude"}>Exclude</option>
              <option value="flag" ?selected=${prep[d.id] === "flag"}>Flag as not ready</option>
            </select>
          </div>`)}
        </div>
      </div>
    </div>`;
    }
    renderAuditResult(r) {
        const icon = { ok: "✓", warn: "⚠", wrong: "✗", missing: "○" };
        const remaining = this.batch.run.blind.attempts_remaining;
        return html `<div class="card">
      <div style="display:flex;gap:14px;align-items:center;margin-bottom:8px">
        <span class="score">${Math.round(r.score_pct ?? 0)}%</span>
        <span class="pill ${r.passed ? "pass" : "fail"}">${r.passed ? "Pass" : "Not yet"}</span>
      </div>
      ${(r.per_field ?? []).map((f) => html `<div class="result-item">
        <span class="ri-icon ${f.status}">${icon[f.status] ?? "•"}</span>
        <div><div class="ri-field">${f.display_label ?? f.field}</div><div class="ri-msg">${f.message}</div></div>
      </div>`)}
      ${r.rationale_md ? html `<div class="prose" style="margin-top:10px">${md(r.rationale_md)}</div>` : nothing}
      ${!r.passed && !this.batch.run.blind.locked ? html `<p>${remaining} attempt${remaining === 1 ? "" : "s"} remaining. Use the step buttons above to revise any step, then submit again.</p>` : nothing}
    </div>`;
    }
    renderLockedOut() {
        return html `<div class="card"><h2>No attempts remaining</h2>
      <p>You have used all your attempts at the audit step. Our support team can review your progress and reset the activity.
        Your coding and audit are kept on file.</p>
      ${this.supportState === "sent" ? html `<p class="ok">Request sent. Support will be in touch by email.</p>`
            : html `<button class="primary" ?disabled=${this.supportState === "sending"} @click=${this.openSupport}>
            ${this.supportState === "sending" ? "Sending…" : "Contact support to unlock"}</button>
          ${this.supportState === "error" ? html `<p class="err">The request could not be sent. Please email student support.</p>` : nothing}`}
    </div>`;
    }
    renderAudit() {
        const ep = this.ep;
        const run = this.batch.run;
        const locked = run.locked_codes?.[ep.scenario_id] ?? [];
        const changes = this.st.audit[ep.scenario_id]?.changes ?? [];
        const labels = this.batch.own_work.labels;
        const a = this.st.arithmetic;
        const setA = (k, v) => this.update_({ arithmetic: { ...this.st.arithmetic, [k]: v } });
        const lockedOut = run.blind.locked;
        return html `<div class="body">
      ${this.renderChartPanes()}
      <div class="pane panel">
        ${this.auditResult ? this.renderAuditResult(this.auditResult) : nothing}
        ${lockedOut ? this.renderLockedOut() : nothing}
        ${this.renderRecordTabs((e) => {
            const l = run.locked_codes?.[e.scenario_id] ?? [];
            const ch = this.st.audit[e.scenario_id]?.changes ?? [];
            return l.length > 0 && l.every((c) => ch.some((x) => x.code === c.code && x.action !== "reorder" && x.action !== "add"));
        })}
        <own-work-audit-record .locked=${locked} .changes=${changes} .labels=${labels} .edition=${ep.edition ?? "13e"}
            ?readonly=${lockedOut}
            @changes-change=${(e) => this.update_({ audit: { ...this.st.audit, [ep.scenario_id]: { changes: e.detail.changes } } })}>
        </own-work-audit-record>
        <div class="card"><h3>Your error rate, across all three records</h3>
          <p>Error rate = (number of errors ÷ number of items checked) × 100. Count every change you recorded as one error.
            Count the items as the procedure describes. Enter the rate to one decimal place.</p>
          <div class="arith">
            <label>Errors<input type="number" min="0" step="1" aria-label="Number of errors" .value=${a.errors} ?disabled=${lockedOut}
                   @input=${(e) => setA("errors", e.target.value)}></label>
            <label>Items checked<input type="number" min="1" step="1" aria-label="Items checked" .value=${a.items} ?disabled=${lockedOut}
                   @input=${(e) => setA("items", e.target.value)}></label>
            <label>Rate %<input type="number" min="0" step="0.1" aria-label="Error rate percent" .value=${a.rate_pct} ?disabled=${lockedOut}
                   @input=${(e) => setA("rate_pct", e.target.value)}></label>
          </div>
        </div>
      </div>
    </div>`;
    }
    renderRespond() {
        const ep = this.ep;
        const reveal = this.batch.run.reveal;
        const rec = reveal?.records[ep.scenario_id];
        const rv = this.st.review;
        const labels = this.batch.own_work.labels;
        const completed = this.batch.run.status === "completed";
        const recResult = this.reviewResult?.records[ep.scenario_id];
        const fmtCof = (c) => (c === 1 ? "arose this admission" : c === 2 ? "pre-existing" : "");
        return html `<div class="body">
      ${this.renderChartPanes()}
      <div class="pane panel">
        ${completed ? html `<div class="card"><h2>Activity complete</h2>
            <p>You have audited your own coding, compared it with the reference and responded to the findings. Continue to the next activity.</p>
            <button class="primary" @click=${this.continueToNext}>Continue →</button></div>` : nothing}
        ${reveal ? html `<div class="card"><h2>Step 5. The reference and your true error rate</h2>
          <p>Your audit passed with ${Math.round(this.auditResult?.score_pct ?? this.batch.run.blind.last?.score_pct ?? 0)}%.
            Across the three records the reference audit found <b>${reveal.rate.errors}</b> error${reveal.rate.errors === 1 ? "" : "s"}
            over <b>${reveal.rate.items}</b> items: a true error rate of <b>${reveal.rate.rate_pct_display}%</b> against an acceptable
            rate of ${reveal.acceptable_pct}%.</p></div>` : nothing}
        ${this.renderRecordTabs((e) => !!this.st.review.corrected[e.scenario_id]?.codes?.length)}
        ${rec ? html `<div class="card"><h3>${rec.title}: findings</h3>
          ${rec.errors.length === 0 ? html `<p class="ok">Nil finding. Your locked coding matched the reference on every item (${rec.items} items checked).</p>`
            : html `<table><thead><tr><th>Finding</th><th>Reference</th><th>Your code</th><th>Impact</th></tr></thead><tbody>
              ${rec.errors.map((e) => html `<tr><td class="err">${e.label}</td><td class="mono">${e.reference ?? ""}${e.expected_cof ? ` (onset ${fmtCof(e.expected_cof)})` : ""}</td>
                <td class="mono">${e.student ?? ""}</td><td>${e.impact ? labels.impacts.find((i) => i.id === e.impact)?.label ?? e.impact : ""}</td></tr>`)}
              </tbody></table>
              <p class="notice">${rec.errors.length} error${rec.errors.length === 1 ? "" : "s"} over ${rec.items} items on this record.</p>`}
          <h3>Reference coding</h3>
          <table><thead><tr><th>Code</th><th>Role</th><th>Onset</th><th></th></tr></thead><tbody>
            ${rec.reference.map((c) => html `<tr><td class="mono">${c.code}${c.accept.length ? ` (or ${c.accept.join(", ")})` : ""}</td><td>${c.type}</td><td>${fmtCof(c.cof)}</td><td>${c.optional ? "optional" : ""}</td></tr>`)}
          </tbody></table>
          <h3>Your locked coding</h3>
          <div class="locked-list">${rec.locked.map((c) => html `<div>${c.code} ${c.type}${c.cof ? ` · ${fmtCof(c.cof)}` : ""}</div>`)}</div>
        </div>` : nothing}
        ${!completed ? html `
          <div class="card"><h3>Amend your coding to match the reference</h3>
            <p>Edit this record's codes below so they match the reference, including the onset flags. Your corrected coding from the audit is the starting point.</p>
            ${recResult && !recResult.ok ? html `<p class="err">Still differs from the reference: ${recResult.problems.map((p) => `${p.code} (${p.problem})`).join(", ")}.</p>` : nothing}
            ${recResult?.ok ? html `<p class="ok">This record matches the reference.</p>` : nothing}
            <mode-abstract .edition=${ep.edition ?? "13e"} .episodePanel=${{ hmv: false, weight: false, query: false }}></mode-abstract>
          </div>
          <div class="card"><h3>Your response to the findings</h3>
            <div class="q"><div class="prompt">Is your true error rate within or over the acceptable rate?</div>
              <label><input type="radio" name="within" value="within" .checked=${rv.within_rate === "within"}
                     @change=${() => this.update_({ review: { ...this.st.review, within_rate: "within" } })}> Within the acceptable rate</label>
              <label><input type="radio" name="within" value="over" .checked=${rv.within_rate === "over"}
                     @change=${() => this.update_({ review: { ...this.st.review, within_rate: "over" } })}> Over the acceptable rate</label>
              ${this.reviewResult && !this.reviewResult.within_rate_ok ? html `<p class="err">Check this against the true rate above.</p>` : nothing}
            </div>
            <div class="q"><div class="prompt">What will you do in response? Select every action that fits your result.</div>
              ${labels.response_actions.map((o) => html `<label><input type="checkbox" value=${o.id} .checked=${rv.actions.includes(o.id)}
                  @change=${(e) => {
            const on = e.target.checked;
            const cur = this.st.review;
            const actions = on ? [...cur.actions.filter((a) => a !== o.id), o.id] : cur.actions.filter((a) => a !== o.id);
            this.update_({ review: { ...cur, actions } });
        }}> ${o.label}</label>`)}
              ${this.reviewResult && !this.reviewResult.actions_ok ? html `<p class="err">These actions do not fit your result. Think about the findings, the principal diagnosis and the rate.</p>` : nothing}
            </div>
            ${rv.actions.includes("name_gap") ? html `<div class="q"><div class="prompt">Which error type is your recurring gap?</div>
              <select aria-label="Recurring gap" .value=${rv.recurring_gap ?? ""} @change=${(e) => this.update_({ review: { ...this.st.review, recurring_gap: e.target.value || undefined } })}>
                <option value="">Choose</option>
                ${labels.error_types.map((t) => html `<option value=${t.id} ?selected=${rv.recurring_gap === t.id}>${t.label}</option>`)}
              </select>
              ${this.reviewResult && !this.reviewResult.recurring_gap_ok ? html `<p class="err">That is not the error type that recurs in your findings.</p>` : nothing}
            </div>` : nothing}
          </div>` : nothing}
      </div>
    </div>`;
    }
    renderActions() {
        const phase = this.st.phase;
        const status = this.batch.run.status;
        const save = html `<span class="save">${this.saveStatus === "saving" ? "Saving…" : this.saveStatus === "saved" ? "Saved" : this.saveStatus === "error" ? "Save failed" : ""}</span>`;
        const notice = this.notice ? html `<span class="notice err">${this.notice}</span>` : nothing;
        const back = phase > 0 && status !== "passed" && status !== "completed"
            ? html `<button class="nav-btn" @click=${() => this.setPhase(phase - 1)}>← Back</button>` : nothing;
        let primary = nothing;
        if (phase === 0)
            primary = html `<button class="primary" @click=${() => this.setPhase(1)}>Start</button>`;
        else if (phase === 1)
            primary = html `<button class="primary" ?disabled=${!this.identifyComplete()} @click=${() => this.setPhase(2)}>Next: code the records →</button>`;
        else if (phase === 2)
            primary = status === "draft"
                ? html `<button class="primary" ?disabled=${this.busy} @click=${() => { this.confirmLock = true; }}>Lock my coding</button>`
                : html `<button class="primary" @click=${() => this.setPhase(3)}>Next: prepare the records →</button>`;
        else if (phase === 3)
            primary = html `<button class="primary" ?disabled=${!this.prepComplete()} @click=${() => this.setPhase(4)}>Next: audit your coding →</button>`;
        else if (phase === 4) {
            const blind = this.batch.run.blind;
            const ready = this.auditDecisionsComplete().ok && this.arithmeticComplete();
            const attempt = blind.attempts_used + 1;
            primary = blind.locked ? html `<span class="notice">No attempts remaining</span>`
                : html `<span class="notice">Attempt ${attempt} of ${blind.max_attempts}${attempt >= blind.max_attempts ? " (final attempt)" : ""}</span>
               <button class="primary" ?disabled=${this.busy || !ready} @click=${() => { this.confirmAudit = true; }}>
                 ${this.busy ? "Marking…" : "Submit my audit"}</button>`;
        }
        else if (phase === 5) {
            primary = status === "completed" ? html `<button class="primary" @click=${this.continueToNext}>Continue →</button>`
                : html `<button class="primary" ?disabled=${this.busy || !this.reviewComplete()} @click=${this.submitReview}>${this.busy ? "Checking…" : "Submit my response"}</button>`;
        }
        return html `<div class="actions">${save}${notice}<span class="spacer"></span>${back}${primary}</div>`;
    }
    renderConfirmLock() {
        const rows = this.codingSummary();
        const blocked = rows.some((r) => !r.ready);
        return html `<div class="confirm-backdrop" role="dialog" aria-modal="true">
      <div class="confirm-modal"><h2>Lock your coding?</h2>
        <p>Once locked, your coding cannot be changed. It becomes the work you audit in the next steps.</p>
        <table><tbody>${rows.map((r) => html `<tr><td>${r.ep.episode_title}</td><td>${r.total} code${r.total === 1 ? "" : "s"}</td>
          <td class="${r.ready ? "ok" : "err"}">${r.ready ? "ready" : "needs a principal diagnosis and at least two further codes"}</td></tr>`)}</tbody></table>
        <div class="confirm-actions">
          <button class="nav-btn" @click=${() => { this.confirmLock = false; }}>Cancel</button>
          <button class="primary" ?disabled=${blocked || this.busy} @click=${this.lock}>${this.busy ? "Locking…" : "Lock"}</button>
        </div></div></div>`;
    }
    renderConfirmAudit() {
        const blind = this.batch.run.blind;
        const attempt = blind.attempts_used + 1;
        return html `<div class="confirm-backdrop" role="dialog" aria-modal="true">
      <div class="confirm-modal"><h2>Submit your audit?</h2>
        <p>This is attempt ${attempt} of ${blind.max_attempts}. Your decisions on every code, your error types and your error rate are marked together.
          ${attempt >= blind.max_attempts ? "This is your final attempt." : ""}</p>
        <div class="confirm-actions">
          <button class="nav-btn" @click=${() => { this.confirmAudit = false; }}>Cancel</button>
          <button class="primary" ?disabled=${this.busy} @click=${this.submitAudit}>Submit</button>
        </div></div></div>`;
    }
    render() {
        if (!this.batch)
            return html `<p style="padding:24px">Loading activity…</p>`;
        const phase = this.st.phase;
        let body;
        switch (phase) {
            case 0:
                body = this.renderBriefing();
                break;
            case 1:
                body = this.renderIdentify();
                break;
            case 2:
                body = this.renderCode();
                break;
            case 3:
                body = this.renderPrepare();
                break;
            case 4:
                body = this.renderAudit();
                break;
            default: body = this.renderRespond();
        }
        return html `
      ${this.renderStepper()}
      ${body}
      ${this.renderActions()}
      ${this.confirmLock ? this.renderConfirmLock() : nothing}
      ${this.confirmAudit ? this.renderConfirmAudit() : nothing}
    `;
    }
};
__decorate([
    property({ attribute: false })
], OwnWorkView.prototype, "batch", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "st", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "recordIdx", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "selectedDocId", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "saveStatus", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "busy", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "notice", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "lockProblems", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "confirmLock", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "confirmAudit", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "auditResult", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "reviewResult", void 0);
__decorate([
    state()
], OwnWorkView.prototype, "supportState", void 0);
OwnWorkView = OwnWorkView_1 = __decorate([
    customElement("own-work-view")
], OwnWorkView);
export { OwnWorkView };
